import { InfiniteListBase, useListContext } from "ra-core";
import { FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * Form submissions matched to this contact - the automation worker's
 * identity pass (worker.ts) is what attaches contact_id to a form.submitted
 * event, so this only ever shows the linked copy, never the raw capture.
 * Reads activity_events directly, same pattern as ContactEmailActivity:
 * renders nothing when there are none, so it adds no noise to contacts who
 * never filled one out.
 */

interface FormSubmissionPayload {
  formId: string;
  fields?: Record<string, string>;
}

interface ActivityEventRecord {
  id: string;
  occurred_at: string;
  payload: FormSubmissionPayload;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

// Already shown elsewhere on the contact record - skip repeating them here.
const SKIP_FIELDS = new Set(["email", "first_name", "last_name"]);

const FormActivityCard = () => {
  const { forms = [] } = useConfigurationContext();
  const { data, isPending } = useListContext<ActivityEventRecord>();
  if (isPending || !data || data.length === 0) return null;

  const nameOf = (formId: string) => forms.find((f) => f.id === formId)?.name ?? formId;

  return (
    <Card>
      <CardContent>
        <h2 className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-3">
          Form fills
        </h2>
        <div className="flex flex-col gap-2.5">
          {data.map((e) => {
            const details = Object.entries(e.payload.fields ?? {})
              .filter(([key, value]) => !SKIP_FIELDS.has(key) && value)
              .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
              .join(" · ");
            return (
              <div
                key={e.id}
                className="flex gap-2 text-sm border-t border-border pt-2.5 first:border-t-0 first:pt-0"
              >
                <FileText className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium truncate">{nameOf(e.payload.formId)}</span>
                    <span className="text-xs text-muted-foreground shrink-0 ml-auto">
                      {fmtDate(e.occurred_at)}
                    </span>
                  </div>
                  {details && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{details}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const ContactFormActivity = ({ contactId }: { contactId: number | string }) => (
  <InfiniteListBase
    resource="activity_events"
    filter={{ contact_id: contactId, type: "form.submitted" }}
    sort={{ field: "occurred_at", order: "DESC" }}
    perPage={10}
    disableSyncWithLocation
    storeKey={false}
    loading={false}
    error={false}
    queryOptions={{
      onError: () => {
        // The demo/FakeRest provider has no activity_events collection - fail
        // silently there (the card just renders nothing) instead of a toast.
      },
    }}
  >
    <FormActivityCard />
  </InfiniteListBase>
);
